import * as Sentry from "@sentry/react";

import { getOrganizationById, getScopeByName } from "@utils/api-communication/RestApiCli";
import {
  logOut,
  setForcePassChange,
  setUserDistributorId,
  setUserOrg,
  setUserRole,
} from "@actions/userActions";
import { updateProductBatch, updateProductScope } from "@actions/productActions";

import { CustomJwtPayload } from "types/authTypes";
import { SecureTokenManager } from "./auth/secureTokenManager";
import { VFABehaviour } from "types/tests";
import axios from "axios";
import { getOrganizationId } from "./AuthUtil";
import { jwtDecode } from "jwt-decode";
import normalValues from "../data/normal_values.json";
import { store } from "@store/index";
import { updatePatient } from "@store/patients/patientSlice";
import dayjs from "dayjs";
import i18next from "i18next";
export * from "./VersionUtils";

export const GPAMinTestCount = 5;

// Device `states` values can arrive boolean or string/number-encoded ("true",
// "1") depending on how the headset serialized them, so normalize permission
// grants at the trust boundary instead of assuming a strict `=== true`.
export function isPermissionGranted(value: unknown): boolean {
  return value === true || value === "true" || value === 1 || value === "1";
}

export function errorCheck(func) {
  if (func) {
    try {
      return func();
    } catch (e) {
      console.error(e);
    }
  }
  return "";
}

export function getDateBefore(unit) {
  switch (unit) {
    case "today":
      return new Date(new Date().setHours(0, 0, 0, 1));
    case "week":
      const weekBefore = new Date();
      weekBefore.setDate(weekBefore.getDate() - 7);
      return weekBefore;
    case "month":
      const monthBefore = new Date();
      monthBefore.setMonth(monthBefore.getMonth() - 1);
      return monthBefore;
    case "year":
      const yearBefore = new Date();
      yearBefore.setFullYear(yearBefore.getFullYear() - 1);
      return yearBefore;
  }
  return new Date();
}

export function sortByCreationDateDesc<T extends { creationDate?: string | Date }>(
  items: T[],
): T[] {
  const creationTime = (item: T) => {
    const time = new Date(item.creationDate ?? 0).getTime();
    return Number.isNaN(time) ? 0 : time;
  };
  return [...items].sort((a, b) => creationTime(b) - creationTime(a));
}

export function inTimeLimits(dateStr, limitInMs = 120000) {
  const date = new Date(dateStr);
  const now = new Date();
  return now.getTime() - date.getTime() < limitInMs;
}

export function arraysEqual(a, b) {
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function getAppLocale(): string | undefined {
  const browserLocale = typeof navigator !== "undefined" ? navigator.language : undefined;
  const appLocale = i18next.language;
  if (!appLocale) {
    return browserLocale;
  }
  if (appLocale.includes("-")) {
    return appLocale;
  }
  const sameLanguage = browserLocale?.toLowerCase().startsWith(`${appLocale.toLowerCase()}-`);
  return sameLanguage ? browserLocale : appLocale;
}

export function formatDateAndTime(dateStr): string {
  return formatDate(dateStr) + " " + formatTime(dateStr);
}

export function formatTime(dateStr): string {
  try {
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    return new Intl.DateTimeFormat(getAppLocale(), {
      hour: "numeric",
      minute: "numeric",
    }).format(date);
  } catch (e) {
    console.error("Error formatting time:", e);
    return "";
  }
}

export function parseDateOnly(value: string | Date): Date {
  if (value instanceof Date) {
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
  }
  const str = String(value).trim();
  const dateOnlyMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const year = parseInt(dateOnlyMatch[1], 10);
    const month = parseInt(dateOnlyMatch[2], 10) - 1;
    const day = parseInt(dateOnlyMatch[3], 10);
    return new Date(year, month, day);
  }
  const d = new Date(str);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function parseDateOnlyOrNull(value: string | Date | null): Date | null {
  return value ? parseDateOnly(value) : null;
}

export function parseDateRangeOnly(
  value: [string | null, string | null] | [Date | null, Date | null],
): [Date | null, Date | null] {
  return [parseDateOnlyOrNull(value[0]), parseDateOnlyOrNull(value[1])];
}

export function isPastDay(value: Date): boolean {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return value.getTime() < startOfToday.getTime();
}

export function endOfDayIsoOrNull(value: Date | null): string | null {
  if (!value) {
    return null;
  }
  const endOfDay = new Date(value);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.toISOString();
}

export type LicenseExpiryDuration = "1w" | "1m" | "1y" | "custom";

export const LICENSE_EXPIRY_DURATIONS: LicenseExpiryDuration[] = ["1w", "1m", "1y", "custom"];

export const DEFAULT_LICENSE_EXPIRY_DURATION: LicenseExpiryDuration = "1y";

export const LICENSE_EXPIRY_DURATION_LABEL_KEY: Record<LicenseExpiryDuration, string> = {
  "1w": "licenseExpiry.oneWeek",
  "1m": "licenseExpiry.oneMonth",
  "1y": "licenseExpiry.oneYear",
  custom: "licenseExpiry.custom",
};

export interface LicenseExpirySelection {
  duration: LicenseExpiryDuration;
  date: Date | null;
  originalIso?: string;
}

export const DEFAULT_LICENSE_EXPIRY_SELECTION: LicenseExpirySelection = {
  duration: DEFAULT_LICENSE_EXPIRY_DURATION,
  date: null,
};

export function expiryDateFromDuration(
  duration: Exclude<LicenseExpiryDuration, "custom">,
  from: Date = new Date(),
): Date {
  const unit = duration === "1w" ? "week" : duration === "1m" ? "month" : "year";
  return dayjs(from).add(1, unit).toDate();
}

export function expiryIsoFromSelection(
  selection: LicenseExpirySelection,
  from: Date = new Date(),
): string | null {
  if (selection.duration === "custom") {
    if (selection.originalIso) {
      return selection.originalIso;
    }
    return endOfDayIsoOrNull(selection.date);
  }
  return endOfDayIsoOrNull(expiryDateFromDuration(selection.duration, from));
}

export function isLicenseExpirySelectionIncomplete(selection: LicenseExpirySelection): boolean {
  return selection.duration === "custom" && (!selection.date || isPastDay(selection.date));
}

export function expirySelectionFromIso(
  value: string | null | undefined,
  now: Date = new Date(),
): LicenseExpirySelection {
  if (!value) {
    return DEFAULT_LICENSE_EXPIRY_SELECTION;
  }
  const requested = new Date(value);
  if (Number.isNaN(requested.getTime()) || requested.getTime() <= now.getTime()) {
    return DEFAULT_LICENSE_EXPIRY_SELECTION;
  }
  return {
    duration: "custom",
    date: new Date(requested.getFullYear(), requested.getMonth(), requested.getDate()),
    originalIso: value,
  };
}

export function formatDateOnly(dateStr: string | Date): string {
  try {
    const date = parseDateOnly(dateStr);
    return new Intl.DateTimeFormat(getAppLocale(), {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).format(date);
  } catch (e) {
    console.error("Error formatting date:", e);
    return "";
  }
}

export function formatLongDate(dateStr: string | Date): string {
  try {
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    return new Intl.DateTimeFormat(getAppLocale(), {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  } catch (e) {
    console.error("Error formatting date:", e);
    return "";
  }
}

export function formatDate(dateStr): string {
  try {
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    return new Intl.DateTimeFormat(getAppLocale(), {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).format(date);
  } catch (e) {
    console.error("Error formatting date:", e);
    return "";
  }
}

export function convertToQueryString(queryMap: {}): string {
  const strList = [];
  for (const queryMapKey in queryMap) {
    strList.push(queryMapKey + "=" + queryMap[queryMapKey]);
  }
  if (strList.length > 0) {
    return "?" + strList.join("&");
  }
  return "";
}

export function isPhoneNoValid(no): boolean {
  const pattern = new RegExp(/^[0-9\b]+$/);
  return pattern.test(no);
}

export function isPatientIdFormatValid(id): boolean {
  const t = id != null ? String(id).trim() : "";
  if (t.length === 0) return false;
  return /^[a-zA-Z0-9](.*[a-zA-Z0-9])?$/.test(t);
}

export function isDobValid(date): boolean {
  const year = date.getFullYear();
  if (year < 1920 || year > new Date().getFullYear()) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime() <= today.getTime();
}

export function isEmailValid(val): boolean {
  const regEmail =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regEmail.test(val);
}

export function getDiffInSec(tsTime): number {
  const now = new Date().getTime();
  const diff = now - tsTime;
  return diff / 1000;
}

export function getDiffInMin(tsTime): number {
  return getDiffInSec(tsTime) / 60;
}

export function calculateAgeForTest(testStartDate: Date, patientDob: Date): number {
  const diff_ms = testStartDate.getTime() - patientDob.getTime();
  const age_dt = new Date(diff_ms);

  return Math.abs(age_dt.getUTCFullYear() - 1970);
}

export function computeAgeBracket(patientDob: Date | string): "child" | "adult" | null {
  const dobDate = patientDob instanceof Date ? patientDob : new Date(patientDob);
  if (!Number.isFinite(dobDate.getTime())) return null;
  return calculateAgeForTest(new Date(), dobDate) < 13 ? "child" : "adult";
}

export function calculateAgeForTestFloat(testStartDate: Date, patientDob: Date): number {
  const diff_ms = testStartDate.getTime() - patientDob.getTime();
  const age_dt = new Date(diff_ms);

  return Number(
    (Math.abs(age_dt.getUTCFullYear() - 1970) + age_dt.getUTCMonth() / 12).toPrecision(3),
  );
}

export function getUserFromLocalStorage(): CustomJwtPayload {
  const token = SecureTokenManager.getToken();
  if (!token) {
    SecureTokenManager.clearTokens();
    return null;
  }

  try {
    const decoded = jwtDecode<CustomJwtPayload>(token); // Only decodes, does not verify signature
    return decoded;
  } catch {
    SecureTokenManager.clearTokens();
    return null;
  }
}

export function checkRole(roles): boolean {
  let isLoggedIn = false;

  const decoded = getUserFromLocalStorage();
  if (decoded == null) {
    return false;
  }
  isLoggedIn = decoded["exp"] > Date.now() / 1000;
  if (isLoggedIn) {
    if (typeof roles === "string") roles = [roles];
    const role = decoded["role"];
    // superadmin subsumes admin, mirroring role.utils.ts on the backend.
    const isAdmin = role === "admin" || role === "superadmin";
    return roles.includes(role) || isAdmin || (role === "manager" && roles.includes("distributor"));
  } else {
    warnNotLoggedIn("checkRole");
    store.dispatch(logOut());
  }
}

function warnNotLoggedIn(methodName: string) {
  if (isDev()) {
    console.warn(
      JSON.stringify({
        message: "User is not logged in.",
        method: methodName,
        time: new Date().toLocaleString(),
      }),
    );
  }
}

export function getRole() {
  let isLoggedIn = false;
  const decoded = getUserFromLocalStorage();
  if (decoded == null) {
    return null;
  }
  isLoggedIn = decoded["exp"] > Date.now() / 1000;

  if (isLoggedIn) {
    const role = decoded["role"];
    return role;
  } else {
    warnNotLoggedIn("getRole");
    store.dispatch(logOut());
  }
}

export function getUsername() {
  let isLoggedIn = false;
  const decoded = getUserFromLocalStorage();
  if (decoded == null) {
    return null;
  }
  isLoggedIn = decoded["exp"] > Date.now() / 1000;
  if (isLoggedIn) {
    const un = decoded["username"];
    return un;
  } else {
    warnNotLoggedIn("getUsername");
    store.dispatch(logOut());
  }
}

export function isLoggedIn(cb?) {
  let isLoggedIn = false;
  const decoded = getUserFromLocalStorage();
  if (decoded == null) {
    Sentry.setUser(null);
    return false;
  }
  localStorage.setItem("orgId", decoded["orgId"]);

  if ((store.getState() as any)?.user?.role !== decoded?.role) {
    store.dispatch(setUserRole(decoded?.role));
  }
  if ((store.getState() as any)?.user?.orgId !== decoded?.orgId) {
    store.dispatch(setUserOrg(decoded?.orgId));
  }
  if ((store.getState() as any)?.user?.distId !== decoded?.distId) {
    store.dispatch(setUserDistributorId(decoded?.distId));
  }
  const state = store.getState();
  const selectedPatient = (state as any)?.patient?.selectedPatient;
  if (selectedPatient?.uuid && !selectedPatient?.orgIds?.includes(decoded?.orgId)) {
    store.dispatch(updatePatient({ orgIds: [decoded?.orgId] }));
  }
  if ((store.getState() as any)?.user?.forcePassChange !== decoded?.forcePassChange) {
    store.dispatch(setForcePassChange(decoded?.forcePassChange));
  }

  isLoggedIn = decoded["exp"] > Date.now() / 1000;
  if (!isLoggedIn) {
    Sentry.setUser(null);
    store.dispatch(logOut("/login"));
    cb?.call();
  } else {
    Sentry.setUser({
      id: decoded.sub,
      email: decoded.email,
      username: decoded.username,
    });
    Sentry.setTag("orgId", decoded.orgId);
  }
  return isLoggedIn;
}

export function displayPatientName(fullNameInLowerCase: string): string {
  try {
    return fullNameInLowerCase
      .split(" ")
      .map((namePiece) => namePiece[0].toUpperCase() + namePiece.slice(1))
      .join(" ");
  } catch (e) {
    console.error(e);
    return fullNameInLowerCase;
  }
}

export function routeToLogin(props) {
  props.history.push("/login");
}

export function getNormalValues(queryMap: {}) {
  const ageRange = (Math.floor(queryMap["age"] / 5) * 5).toString();
  let points;
  let normals = normalValues as any;
  const behavior = queryMap["testBehaviour"];
  if (behavior && String(behavior) === String(VFABehaviour.SWAP_TOP)) {
    if (normals["swap"]) {
      normals = normals["swap"];
    }
  }
  const eye = queryMap["eye"];
  const testType = queryMap["testType"];
  if (normals && normals[testType] && normals[testType][eye] && normals[testType][eye][ageRange]) {
    points = normals[testType][eye][ageRange];
  } else {
    points = Array.from(Array(54), () => 32);
  }
  return points;
}

export function isDev() {
  return (
    process.env.NODE_ENV === "development" ||
    (window.location.href && window.location.href.includes("dev"))
  );
}

export function isProd() {
  return process.env.NODE_ENV === "production";
}

/* Gets Version Info From data.json */

export function resetProductSelection(
  pid,
  version,
  puiVersion,
  resetStatus = true,
  scopeName = null,
  volume = null,
  alias = null,
  battery = null,
) {
  const toBeUpdated = {
    pid,
    alias,
    currentVersion: version,
    osVersion: puiVersion,
    controllers: null,
    controllerBattery: null,
    scene: "",
    permissions: null,
  };
  if (resetStatus) {
    toBeUpdated["status"] = -1;
    toBeUpdated["deviceStatus"] = -1;
  }
  if (volume !== null && volume !== undefined) {
    toBeUpdated["volume"] = typeof volume === "number" ? volume : parseInt(volume, 10);
  }
  if (battery !== null && battery !== undefined) {
    toBeUpdated["battery"] = typeof battery === "number" ? battery : parseInt(battery, 10);
  }
  store.dispatch(updateProductBatch(toBeUpdated));

  updateScopeList({ scopeName, orgId: getOrganizationId() });
}

export function updateScopeList(by: { scopeName?: string; orgId?: string; scopeList?: string[] }) {
  if (by.scopeName) {
    getScopeByName(by.scopeName, (response) => {
      if (response.data) {
        store.dispatch(updateProductScope(response.data.list));
      }
    });
  } else if (by.orgId) {
    // If orgId is provided, fetch the scope name from that organization
    getOrganizationById(by.orgId, (response) => {
      const scopeNameFromOrg = response.data?.scopeName;
      if (scopeNameFromOrg) {
        updateScopeList({ scopeName: scopeNameFromOrg });
      } else {
        // If no scopeName is found, use default scope list
        store.dispatch(updateProductScope(["newUI", "VFA"]));
      }
    });
  } else if (by.scopeList) {
    store.dispatch(updateProductScope(by.scopeList));
  }
}
/**
 * Deeply compares two objects for equality
 * @param obj1 First object to compare
 * @param obj2 Second object to compare
 * @returns Boolean indicating if objects are equal
 */
export function areObjectsEqual(obj1: any, obj2: any): boolean {
  // Check if identical reference or both are null/undefined
  if (obj1 === obj2) return true;

  // If either is null or not an object, they're not equal
  if (!obj1 || !obj2 || typeof obj1 !== "object" || typeof obj2 !== "object") return false;

  // Check if types match
  if (obj1.constructor !== obj2.constructor) return false;

  // Special handling for Date objects
  if (obj1 instanceof Date) return obj1.getTime() === obj2.getTime();

  // Compare object keys
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  // Recursively compare each property
  return keys1.every((key) => keys2.includes(key) && areObjectsEqual(obj1[key], obj2[key]));
}

export async function getAllCountryNames(): Promise<string[]> {
  const response = await axios.get("https://restcountries.com/v3.1/all?fields=name");
  if (response && response.data && typeof response.data == typeof []) {
    const countryNames = response?.data?.map((country) => country?.name?.common);
    return countryNames?.sort() ?? [];
  }
  return [];
}
